import { Button } from "@/components/ui/button";
import {
  DownloadIcon,
  ListFilter,
  LoaderCircle,
  MoreHorizontal,
  Trash2,
  UploadIcon,
} from "lucide-react";
import { useGetPursuitDocs } from "./useGetPursuitDocs";
import { useOutletContext, useParams } from "react-router-dom";
import CardLoader from "@/ui/CardLoader";
import { DataTable } from "@/components/ui/datatable";
import { DataTableColumnHeader } from "@/components/ui/database-column-header";
import { formatDate2, formatFileSize, toPascalCase } from "@/utils/helpers";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import { useMemo, useRef, useState } from "react";
import { useUploadPursuitDocs } from "./useUploadPursuitDocs";
import { useDeletePursuitDoc } from "./useDeletePursuitDoc";
import { downloadPursuitDoc } from "./api/apiPursuitDocs";

function DeleteDocumentDropDownItem({ docId }) {
  const { pursuit_id } = useParams();
  const { pursuit } = useOutletContext();
  const { mutate: deletePursuitDoc, isLoading } =
    useDeletePursuitDoc(pursuit_id, pursuit.search_type, pursuit.proposal_id);
  return (
    <DropdownMenuItem
      onClick={() =>
        deletePursuitDoc(docId, pursuit.search_type, pursuit.proposal_id)
      }
      disabled={isLoading}
    >
      <div className="flex gap-2 items-center hover:text-destructive">
        <Trash2 className="size-4 text-destructive" /> Delete
      </div>
    </DropdownMenuItem>
  );
}

const docColumns = [
  {
    accessorKey: "filename",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Name"
        className="w-[400px]"
      />
    ),
    cell: ({ row }) => (
      <div className="font-medium">
        {toPascalCase(row.getValue("filename"))}
      </div>
    ),
  },
  {
    accessorKey: "source",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Source" />
    ),
    cell: ({ row }) => (
      <div className="text-muted-foreground">
        {toPascalCase(row.getValue("source"))}
      </div>
    ),
  },
  {
    accessorKey: "size",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Size" />
    ),
    cell: ({ row }) => (
      <div className="text-muted-foreground">
        {formatFileSize(row.getValue("size"))}
      </div>
    ),
  },
  {
    accessorKey: "uploaded_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Uploaded" />
    ),
    cell: ({ row }) => (
      <div className="text-muted-foreground">
        {formatDate2(row.getValue("uploaded_at"))}
      </div>
    ),
  },

  {
    id: "actions",
    enableHiding: false,

    cell: ({ row }) => {
      const link = row.original.link;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              onClick={() =>
                link
                  ? window.open(link, "_blank")
                  : downloadPursuitDoc(row.original.id, row.original.filename)
              }
            >
              <div className="flex gap-2 items-center">
                <DownloadIcon className="size-4" /> Download
              </div>
            </DropdownMenuItem>
            <DeleteDocumentDropDownItem docId={row.original.id} />
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

const documentTypes = [
  {
    value: "attachment",
    label: (
      <Badge variant="secondary" className="rounded-md">
        <span className="truncate">Attachments</span>
      </Badge>
    ),
  },
  {
    value: "upload",
    label: (
      <Badge variant="secondary" className="rounded-md">
        <span className="truncate">Uploaded</span>
      </Badge>
    ),
  },
];

export default function PursuitDocuments() {
  const { pursuit } = useOutletContext();
  const { data, isLoading } = useGetPursuitDocs(pursuit.id);
  const { mutate: uploadFiles, isLoading: isUploading } = useUploadPursuitDocs(
    pursuit.id,
    pursuit.search_type,
    pursuit.proposal_id
  );
  const [activeFilters, setActiveFilters] = useState({
    source: [],
  });
  const fileInputRef = useRef(null);

  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((item) => {
      const matchesSource =
        activeFilters.source.length === 0 ||
        activeFilters.source.includes(item.source);

      return matchesSource;
    });
  }, [data, activeFilters]);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);

    uploadFiles(files);
  };

  if (isLoading) {
    return <CardLoader />;
  }
  return (
    <div>
      <div className="px-8 py-2 flex items-center justify-between mb-4 gap-4">
        <div className="flex items-center gap-4"></div>
        <div className="flex items-center gap-4">
          {/* <div className="grid grid-cols-[auto_1fr_1fr] grid-rows-[2rem] items-center border rounded-md"> */}
          <div className="flex h-9 items-center border rounded-md">
            <span className="flex items-center gap-1 text-muted-foreground bg-muted/50 h-full px-2">
              <ListFilter className="h-4 w-4" />
              Filters
            </span>
            <MultiSelect
              options={documentTypes}
              onValueChange={(values) => {
                setActiveFilters({ ...activeFilters, source: values });
              }}
              placeholder={
                <span className="flex items-center hover:text-foreground gap-1">
                  Source
                </span>
              }
              maxCount={1}
              className="border-x border-y-0 rounded-none min-h-8 hover:bg-muted h-full"
            />
          </div>
          <Button
            onClick={() => fileInputRef.current.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <LoaderCircle className="animate-spin" />
                <span className="animate-pulse">Uploading...</span>
              </>
            ) : (
              <>
                <UploadIcon />
                Upload Files
              </>
            )}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx"
            multiple
          />
        </div>
      </div>
      <DataTable
        columns={docColumns}
        data={filteredData}
        isLoading={isLoading}
      />
    </div>
  );
}
